import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { FremdKandidaten, FremdKontoStand } from "@kundeninfo/api-client";
import { Icon, Modal, ModalFuss, useBestaetigung, useSofortPopup, useTelefon } from "@kundeninfo/ui";
import { Auswahlmenue, Avatar as MeAvatar, Hinweis, Tabelle } from "@kundeninfo/ui/me-design";
import { KiKnopf, LoeschKnopf } from "../../components/meHuellen.js";
import { formatZeitpunkt } from "../../format.js";
import { personOption } from "../tickets/Avatar.js";
import type { FremdkontenClient } from "./fremdClient.js";
import "./Fremd.css";

/**
 * ZUORDNUNG AM KONTO (Spec Tickets, Nachtrag 1f, F138, Entscheid 217 „plain vanilla"): je eingerichtete Instanz
 * das eigene Konto in GitLab bzw. YouTrack — ausgewählt aus der Liste, die der Server über das Dienstkonto liefert
 * (`Auswahlmenue` mit Suchfeld ab 8 Konten), nie als Freitext. Geprüft wird das Konto beim Speichern am Server
 * (422 `FREMDKONTO_UNBEKANNT`/`FREMDKONTO_VERGEBEN` stehen am Feld). Speichern im strikten `Modal` (216c: keine
 * eigene Bestätigung), Entfernen erst nach gestapelter Bestätigung (F138, Hausregel „Bestätigung vor
 * Datenänderung"): Die gespiegelten Fremdtickets samt Verlauf und Meldungen verschwinden dann aus dem Kundeninfo,
 * im Quellsystem ändert sich nichts.
 *
 * Entscheid 229 c (Gesamtreview M2): Selbst zuordnen nur ein Konto mit der eigenen E-Mail. Der Server liefert
 * ohne ADMIN nur solche Konten; ist die Liste leer, steht dort der Hinweis „… die Verwaltung kann es Dir
 * zuordnen." und Speichern bleibt gesperrt. Ein 403 `FREMDKONTO_NICHT_DEIN` (z. B. Adresse inzwischen geändert)
 * steht am Feld. `admin` (Sitzung) unterscheidet in den Einstellungen: ADMIN sieht alle Konten.
 *
 * Entscheid 231: Ist die eigene E-Mail im Login nicht bestätigt (OIDC `email_verified`), liefert der Server ohne ADMIN
 * eine leere Liste mit `hinweis` — dessen `message` steht dann als Hinweis da (Servertext, nicht nachgebildet), die
 * Auswahl zeigt „E-Mail-Adresse nicht bestätigt", Speichern bleibt gesperrt. Ein 403 `FREMDKONTO_EMAIL_UNBESTAETIGT`
 * steht wie jede Abweisung mit `fields` am Feld.
 *
 * Ohne `userId` die eigenen Zuordnungen (Einstellungen › GitLab und YouTrack) mit „Jetzt abgleichen" (F141); mit
 * `userId` die eines anderen (Verwaltung › Benutzer, nur ADMIN — der Server prüft das ohnehin). Block `fk__`.
 *
 * Drossel (F141): „Jetzt abgleichen" ist gesperrt bis `naechsterManuellAb` aus `GET /api/fremdkonten` (ab
 * Seitenaufruf, auch nach dem Zuordnen, das die Drossel schon stempelt), nach einem Abgleich bis `naechsterAb`,
 * nach einer 429 bis `jetzt + retryAfter`. Die Zeit steht sichtbar daneben („Wieder möglich ab 14:32.") und
 * zusätzlich im Sofort-Popup — Touch und Tastatur erreichen einen gesperrten Knopf nicht (Review Aufgabe 8 B3).
 * Nach dem Zuordnen kann der Server den Sofortabgleich wegen derselben Drossel auslassen — die Zuordnung steht
 * trotzdem; dann holt der nächste Lauf die Fremdtickets, und der Block sagt das.
 *
 * Veraltete Antworten (Review Aufgabe 8 B2): Jedes Laden und jedes Öffnen des Dialogs trägt eine laufende Nummer;
 * eine Antwort, die nicht mehr zum letzten Aufruf gehört (Personenwechsel, Aushängen, zweiter Dialog), wird
 * verworfen. Verwaltung › Benutzer hängt den Block zusätzlich mit `key` je Person ein.
 */
const SYSTEM: Record<FremdKontoStand["system"], string> = { GITLAB: "GitLab", YOUTRACK: "YouTrack" };

/** Code und Text von 403 `FREMDKONTO_NICHT_DEIN` (Entscheid 229 c) — NACHGEBILDET aus `FREMD_FEHLER.NICHT_DEIN` und
 *  `FREMD_NICHT_DEIN_TEXT` in `@kundeninfo/domain` (web darf domain nicht laden, scripts/check-boundaries.mjs). Die
 *  E2E-Spec tickets-fremd prüft, dass der Text der 403 und der Hinweis hier gleich lauten. */
/** „1 Fremdticket", sonst „n Fremdtickets" (Sichtprobe 1g, Befund 4). */
export function fremdticketsZahl(n: number): string {
  return `${n} ${n === 1 ? "Fremdticket" : "Fremdtickets"}`;
}

export const NICHT_DEIN = "FREMDKONTO_NICHT_DEIN";
export const NICHT_DEIN_TEXT = "Dieses Konto gehört nicht zu Deiner E-Mail-Adresse — die Verwaltung kann es Dir zuordnen.";

const UHRZEIT = new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit" });

type Meldung = { art: "ok" | "info" | "warnung" | "fehler"; text: string };

function meldungAus(f: unknown, sonst: string): string {
  return f instanceof ApiError ? f.message : sonst;
}

/**
 * Das Konto in EINER Zeile wie die Beobachterzeile (Sichtprobe 1f, Befund 4): Kreis und „Name (Login)“, zu lang mit
 * Auslassung, der volle Wert im Sofort-Popup. Der Kreis nur als Bild (Initialen, Nutzerfarbe), der Text in Light DOM.
 */
function KontoZeile(p: { name: string; login: string; id: string }): ReactElement {
  const text = `${p.name} (${p.login})`;
  const popup = useSofortPopup<HTMLSpanElement>(text);
  return (
    <span className="fk__konto">
      <span aria-hidden="true" className="fk__kreis"><MeAvatar name={p.name} nutzerId={p.id} /></span>
      <span ref={popup} className="fk__kontotext">
        <span className="fk__name">{p.name}</span> <span className="fk__login">({p.login})</span>
      </span>
    </span>
  );
}

/** Leiser Knopf (seit me-design 2853b23 wieder leise: `--text-leise` dunkel #918f89 hält 4,5:1 auch auf der
 *  Zebrazeile — der Übergang „sekundär statt leise“ aus Sichtprobe 1f, Befund 1, ist zurückgebaut); als Symbol mit
 *  Sofort-Popup, das den Text nennt. */
function AendernKnopf(p: { name: string; text: string; icon: string; symbol: boolean; deaktiviert: boolean;
                           onClick: () => void }): ReactElement {
  const popup = useSofortPopup<HTMLSpanElement>(p.symbol ? p.text : "");
  return (
    <span ref={popup} className="fk__knopf">
      <KiKnopf art="leise" name={p.name} deaktiviert={p.deaktiviert} onClick={p.onClick}>
        {p.symbol ? <Icon name={p.icon} size={14} /> : p.text}
      </KiKnopf>
    </span>
  );
}

export function FremdkontenBlock(p: { client: FremdkontenClient; userId?: string; name?: string; admin?: boolean }):
    ReactElement {
  const selbst = p.userId === undefined;
  /** Nur Konten mit der eigenen E-Mail (Entscheid 229 c): selbst und ohne ADMIN. */
  const nurEigene = selbst && p.admin !== true;
  // Sichtprobe 1f, Befund 5: am Telefon (Karten) stehen die Aktionen rechts in der Kopfzeile der Karte (System),
  // nicht als eigene Zeile, in der der Papierkorb allein umbrach.
  const telefon = useTelefon();
  const [stand, setStand] = useState<FremdKontoStand[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<Meldung | null>(null);
  const [abgleich, setAbgleich] = useState<Meldung | null>(null);
  const [abgleichLaeuft, setAbgleichLaeuft] = useState(false);
  /** Bis wann „Jetzt abgleichen" gesperrt ist (Millisekunden), `null` = frei. */
  const [gesperrtBis, setGesperrtBis] = useState<number | null>(null);
  const [offen, setOffen] = useState<FremdKontoStand | null>(null);
  const [kandidaten, setKandidaten] = useState<FremdKandidaten["items"] | null>(null);
  /** Entscheid 231: Servertext aus `hinweis` der Kandidatenliste (E-Mail im Login nicht bestätigt), sonst `null`. */
  const [kandidatenHinweis, setKandidatenHinweis] = useState<string | null>(null);
  const [wahl, setWahl] = useState("");
  const [feldFehler, setFeldFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const { bestaetigen } = useBestaetigung();
  const sperrPopup = useSofortPopup<HTMLSpanElement>(
    gesperrtBis === null ? "" : `Wieder möglich ab ${UHRZEIT.format(new Date(gesperrtBis))}`);

  /** Laufende Nummern des letzten Ladens bzw. Öffnens — ältere Antworten werden verworfen. */
  const ladeNr = useRef(0);
  const oeffneNr = useRef(0);

  const laden = useCallback(async () => {
    const nr = ++ladeNr.current;
    try {
      const r = await p.client.listFremdkonten(p.userId);
      if (nr !== ladeNr.current) return;
      setStand(r.items);
      setFehler(null);
      if (p.userId === undefined) {
        const ab = r.naechsterManuellAb === null ? Number.NaN : Date.parse(r.naechsterManuellAb);
        setGesperrtBis(Number.isFinite(ab) && ab > Date.now() ? ab : null);
      }
    } catch (f) {
      if (nr !== ladeNr.current) return;
      setFehler(meldungAus(f, "Die Zuordnungen konnten nicht geladen werden."));
    }
  }, [p.client, p.userId]);
  useEffect(() => {
    void laden();
    // Aushängen oder anderer Nutzer: jede noch laufende Antwort gilt als veraltet.
    return () => { ladeNr.current += 1; oeffneNr.current += 1; };
  }, [laden]);

  // Die Sperre löst sich zur angegebenen Zeit von selbst.
  useEffect(() => {
    if (gesperrtBis === null) return undefined;
    const rest = gesperrtBis - Date.now();
    if (rest <= 0) { setGesperrtBis(null); return undefined; }
    const uhr = setTimeout(() => setGesperrtBis(null), rest);
    return () => clearTimeout(uhr);
  }, [gesperrtBis]);

  async function oeffnen(s: FremdKontoStand): Promise<void> {
    const nr = ++oeffneNr.current;
    setOffen(s); setWahl(s.konto?.externalUserId ?? ""); setFeldFehler(null); setKandidaten(null); setMeldung(null);
    setKandidatenHinweis(null);
    try {
      const r = await p.client.listFremdkontoKandidaten(s.system, s.instanceKey);
      if (nr !== oeffneNr.current) return;
      setKandidaten(r.items);
      setKandidatenHinweis(r.hinweis?.message ?? null);
    } catch (f) {
      if (nr !== oeffneNr.current) return;
      setFeldFehler(meldungAus(f, "Die Konten konnten nicht geladen werden."));
      setKandidaten([]);
    }
  }

  async function speichern(): Promise<void> {
    if (offen === null || wahl === "") return;
    setLaeuft(true);
    try {
      const neu = await p.client.setFremdkonto({ ...(selbst ? {} : { userId: p.userId! }), system: offen.system,
                                                 instance: offen.instanceKey, externalUserId: wahl });
      setMeldung(neu.lastSyncAt === null
        ? { art: "info", text: `${neu.label} zugeordnet. Die Fremdtickets kommen mit dem nächsten Abgleich.` }
        : { art: "ok", text: `${neu.label} zugeordnet.` });
      setOffen(null);
      // Neu laden statt die Zeile zu ersetzen: Das Zuordnen stempelt die Drossel (Task 7 M1) — erst die Liste
      // bringt `naechsterManuellAb` und damit die Sperre von „Jetzt abgleichen" (Review Aufgabe 8 B1). Die Liste
      // wird ersetzt, nicht geleert: kein Sprung.
      await laden();
    } catch (f) {
      if (f instanceof ApiError && f.code === NICHT_DEIN) setFeldFehler(NICHT_DEIN_TEXT);
      else {
        setFeldFehler(f instanceof ApiError && f.fields?.[0] !== undefined ? f.fields[0].message
          : meldungAus(f, "Die Zuordnung ließ sich nicht speichern."));
      }
    } finally {
      setLaeuft(false);
    }
  }

  async function entfernen(s: FremdKontoStand): Promise<void> {
    const ja = await bestaetigen({ titel: `Zuordnung ${s.label} entfernen?`, bestaetigenText: "Entfernen",
      text: `${s.tickets === 1 ? "Das gespiegelte Fremdticket" : `Die ${s.tickets} gespiegelten Fremdtickets`} aus ${s.label} ${s.tickets === 1 ? "verschwindet" : "verschwinden"} samt Verlauf und Meldungen aus dem Kundeninfo. In ${SYSTEM[s.system]} ändert sich nichts.` });
    if (!ja) return;
    setMeldung(null);
    try {
      await p.client.removeFremdkonto(s.system, s.instanceKey, p.userId);
      await laden();
      setMeldung({ art: "ok", text: `Zuordnung ${s.label} entfernt.` });
    } catch (f) {
      setMeldung({ art: "fehler", text: meldungAus(f, "Die Zuordnung ließ sich nicht entfernen.") });
    }
  }

  async function jetzt(): Promise<void> {
    setAbgleich(null);
    setAbgleichLaeuft(true);
    try {
      const r = await p.client.syncFremdtickets();
      setAbgleich({ art: r.fehlgeschlagen > 0 ? "warnung" : "ok",
        text: `${r.gelesen} gelesen, ${r.neu} neu.${r.fehlgeschlagen > 0 ? ` ${r.fehlgeschlagen} Konten nicht erreichbar.` : ""}` });
      const ab = Date.parse(r.naechsterAb);
      setGesperrtBis(Number.isFinite(ab) && ab > Date.now() ? ab : null);
      await laden();
    } catch (f) {
      if (f instanceof ApiError && f.status === 429 && f.retryAfter !== undefined) {
        // Die Sperre spricht für sich („Wieder möglich ab …" neben dem Knopf).
        setGesperrtBis(Date.now() + f.retryAfter * 1000);
      } else {
        setAbgleich({ art: f instanceof ApiError && f.status === 429 ? "info" : "fehler",
          text: meldungAus(f, "Der Abgleich ist gescheitert.") });
      }
    } finally {
      setAbgleichLaeuft(false);
    }
  }

  /** Selbst, ohne ADMIN, und kein Konto trägt die eigene E-Mail (Entscheid 229 c): Hinweis, Speichern gesperrt. */
  const keinEigenes = nurEigene && kandidaten !== null && kandidaten.length === 0 && feldFehler === null;
  /** Entscheid 231: E-Mail im Login nicht bestätigt — der Hinweis des Servers ersetzt den aus 229 c. */
  const unbestaetigt = kandidatenHinweis !== null;
  const titel = selbst ? "GitLab und YouTrack" : `GitLab und YouTrack von ${p.name ?? ""}`.trim();
  return (
    <section className={selbst ? "fk" : "fk fk--verwaltung"} aria-label={titel}>
      {!selbst && <h3 className="fk__titel">GitLab und YouTrack</h3>}
      <p className="fk__hilfe">
        {selbst
          ? "Wähle Dein Konto in GitLab und YouTrack. Die Vorgänge, die Dir dort zugewiesen sind, erscheinen als Fremdtickets im Board und in der Liste."
          : "Konten dieser Person in GitLab und YouTrack — ihre zugewiesenen Vorgänge erscheinen als Fremdtickets. Sofort wirksam."}
      </p>
      {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
      {meldung !== null && <Hinweis art={meldung.art}>{meldung.text}</Hinweis>}
      {stand === null && fehler === null && <p className="fk__leer">Lädt …</p>}
      {stand !== null && stand.length === 0 && <p className="fk__leer">Es ist weder GitLab noch YouTrack eingerichtet.</p>}
      {stand !== null && stand.length > 0 && (
        <Tabelle stapeln dicht>
          <table>
            <thead><tr><th scope="col">System</th><th scope="col" className="fk__sp-konto">Konto</th><th scope="col">Abgleich</th>
              {!telefon && <th scope="col" className="fk__sp-aktionen"><span className="fk__unsichtbar">Aktionen</span></th>}</tr></thead>
            <tbody>
              {stand.map((s) => {
                const aktionen: ReactNode = (
                  <span className="fk__aktionen">
                    {/* Zugeordnet: Stift-Symbol „Ändern …“ neben dem Papierkorb (Popup nennt es) — so passen die Aktionen
                        rechts auch in den schmalen Block der Verwaltung und in die Kopfzeile der Karte am Telefon.
                        Nicht zugeordnet: „Zuordnen …“ als Text, am Telefon als Plus. */}
                    <AendernKnopf name={`${s.label} zuordnen …`} deaktiviert={!s.hostFreigegeben}
                      onClick={() => { void oeffnen(s); }} symbol={s.konto !== null || telefon}
                      text={s.konto === null ? "Zuordnen …" : "Ändern …"} icon={s.konto === null ? "plus" : "pen"} />
                    {s.konto !== null && (
                      <LoeschKnopf name={`Zuordnung ${s.label} entfernen`} onClick={() => { void entfernen(s); }} />
                    )}
                  </span>
                );
                return (
                  <tr key={`${s.system}|${s.instanceKey}`}>
                    <td data-label="System">
                      {telefon ? <span className="fk__kopf"><span className="fk__system">{s.label}</span>{aktionen}</span> : s.label}
                    </td>
                    <td className="fk__zelle-konto" data-label="Konto">
                      {s.konto === null ? <span className="fk__leer">nicht zugeordnet</span>
                        : <KontoZeile name={s.konto.name} login={s.konto.login} id={s.konto.externalUserId} />}
                    </td>
                    <td data-label="Abgleich">
                      {/* EIN Träger für die ganze Zelle: Gestapelt (Telefon) legte me-tabelle sonst jede Zusatzzeile
                          (Warnung, Label-Hinweis) als eigenes Rasterteil in die Beschriftungsspalte (Sichtprobe 1g). */}
                      <span className="fk__abgleichzelle">
                        {!s.hostFreigegeben && <span className="fk__warnung">Host nicht freigegeben</span>}
                        {/* Zeitpunkt und Anzahl je eine ungebrochene Zeile: Die Spalte bleibt schmal und bricht nicht
                            mitten im Datum (Sichtprobe 1f, Befund 4). */}
                        {s.hostFreigegeben && (s.lastSyncAt === null ? "–" : (
                          // Ein Träger: Gestapelt legt me-tabelle jedes Kind der Zelle als eigenes Rasterteil an.
                          <span className="fk__stand">
                            <span className="fk__zeile">{formatZeitpunkt(s.lastSyncAt)}</span>{" "}
                            <span className="fk__zeile">{fremdticketsZahl(s.tickets)}</span>
                          </span>
                        ))}
                        {s.lastSyncError !== null && <span className="fk__warnung">{s.lastSyncError}</span>}
                        {/* Frage 239: dass die Verwaltung Labels ausschließt — welche, sieht nur sie. */}
                        {s.ausgeschlosseneLabels > 0 && (
                          <span className="fk__leise">Vorgänge mit bestimmten Labels blendet die Verwaltung aus.</span>
                        )}
                      </span>
                    </td>
                    {!telefon && <td className="fk__zelle-aktionen" data-label="Aktionen">{aktionen}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Tabelle>
      )}
      {selbst && stand !== null && stand.some((s) => s.konto !== null) && (
        <div className="fk__abgleich">
          {/* Sofort-Popup am Rahmen: Ein gesperrter Knopf selbst meldet kein Überfahren. */}
          <span ref={sperrPopup} className="fk__sperre">
            <KiKnopf deaktiviert={abgleichLaeuft || gesperrtBis !== null} onClick={() => { void jetzt(); }}>
              Jetzt abgleichen
            </KiKnopf>
          </span>
          {abgleichLaeuft && <Hinweis art="info">Gleicht ab …</Hinweis>}
          {!abgleichLaeuft && abgleich !== null && <Hinweis art={abgleich.art}>{abgleich.text}</Hinweis>}
          {!abgleichLaeuft && gesperrtBis !== null && (
            <span className="fk__sperrzeit">Wieder möglich ab {UHRZEIT.format(new Date(gesperrtBis))}.</span>
          )}
        </div>
      )}
      {offen !== null && (
        <Modal titel={`${offen.label}: Konto zuordnen`} onAbbrechen={() => setOffen(null)} abbrechenGesperrt={laeuft}>
          <p className="fk__hilfe">
            {nurEigene
              ? `Zur Auswahl steht das Konto in ${SYSTEM[offen.system]}, das Deine E-Mail-Adresse trägt. Beim Speichern wird es dort noch einmal geprüft.`
              : `Die Liste kommt aus ${SYSTEM[offen.system]} (Dienstkonto). Beim Speichern wird das Konto dort noch einmal geprüft.`}
          </p>
          {unbestaetigt && feldFehler === null && <Hinweis art="info">{kandidatenHinweis}</Hinweis>}
          {keinEigenes && !unbestaetigt && <Hinweis art="info">{NICHT_DEIN_TEXT}</Hinweis>}
          <Auswahlmenue beschriftung="Konto" sucheAb={8} wert={wahl}
            platzhalter={kandidaten === null ? "Lädt …" : kandidaten.length === 0
              ? (unbestaetigt ? "E-Mail-Adresse nicht bestätigt"
                : nurEigene ? "Kein Konto mit Deiner E-Mail-Adresse" : "Keine Konten gefunden") : "Bitte wählen"}
            {...(feldFehler !== null ? { fehler: feldFehler } : {})}
            onGeaendert={(d) => { setWahl(d.wert); setFeldFehler(null); }}>
            {(kandidaten ?? []).map((k) => (
              <option key={k.externalUserId} value={k.externalUserId}
                {...personOption({ id: k.externalUserId, name: k.name })} data-kuerzel={k.login}>{k.name}</option>
            ))}
          </Auswahlmenue>
          <ModalFuss speichernText="Speichern" onSpeichern={() => { void speichern(); }} onAbbrechen={() => setOffen(null)}
            speichernGesperrt={wahl === "" || laeuft || keinEigenes || unbestaetigt} abbrechenGesperrt={laeuft} />
        </Modal>
      )}
    </section>
  );
}
